// 자동 채우기 진행 패널(AutoFillProgress) 표시용 — 생각 요약·계획 글 나누기, 흘러오는 마크다운 줄 해석
import type { AutoFillStep } from "hooks/useAutoFillStream";

// 생각기록지 계획 단계 (계약 3.4)
export const PLAN_STEP = "plan";

const HANGUL = /[가-힣]/;

// 단계의 생각 요약 글을 계획(한국어)과 모델 생각(영어)으로 나눈다.
// 글 종류(metadata.kind)가 오면 그대로 — 계획은 그 단계의 마지막 "plan" 글 (계약 3.9).
// 안 오면(예전 Agent) 계획 단계의 마지막 글 — 생각 요약이 먼저, 계획이 그 뒤 별도 메시지로 온다.
// 이때 단계가 진행 중이면 앞에 글이 있거나 한글이 보일 때만 계획으로 본다 (먼저 오는 생각 요약이 잠깐 계획으로 보이지 않게)
export function splitStepReasoning(
  step: Pick<AutoFillStep, "name" | "status" | "reasoning">
) {
  if (step.reasoning.some((message) => message.kind)) {
    const plans = step.reasoning.filter((message) => message.kind === "plan");
    return {
      plan: plans[plans.length - 1]?.text,
      thoughts: step.reasoning
        .filter((message) => message.kind !== "plan" && message.text.trim())
        .map((message) => message.text),
    };
  }
  const messages = step.reasoning.map((message) => message.text);
  const last = messages.length - 1;
  const hasPlan =
    step.name === PLAN_STEP &&
    last >= 0 &&
    (step.status === "done" || last > 0 || HANGUL.test(messages[last]));
  return {
    plan: hasPlan ? messages[last] : undefined,
    thoughts: (hasPlan ? messages.slice(0, last) : messages).filter((text) =>
      text.trim()
    ),
  };
}

export type ReasoningLine =
  | { type: "heading"; text: string }
  | { type: "item"; marker: string; depth: number; text: string }
  | { type: "text"; text: string };

// 계획 끝에 붙는 제목 (Agent prompts/plan_coach_points.md)
const COACH_POINTS_TITLE = "코치가 짚을 거리";

// 흘러오는 중 아직 닫히지 않은 ** 는 뗀다 — 닫히면 renderInlineBold 가 굵게 그린다
export function hideOpenBold(text: string) {
  const index = text.lastIndexOf("**");
  return text.split("**").length % 2 === 0
    ? text.slice(0, index) + text.slice(index + 2)
    : text;
}

// 생각 요약·계획 글을 줄 단위로 — 제목(# …, 한 줄 전체 **…**, "코치가 짚을 거리"), 목록(- · * · 1.), 그 밖의 글. 빈 줄은 뺀다
export function parseReasoningLines(text: string): ReasoningLine[] {
  return text.split(/\r?\n/).flatMap((raw): ReasoningLine[] => {
    const line = hideOpenBold(raw.trimEnd());
    if (!line.trim()) return [];

    const heading = line.match(/^\s*#{1,6}\s+(.*)$/);
    if (heading) return [{ type: "heading", text: heading[1] }];

    const item = line.match(/^(\s*)([-*•]|\d+[.)])\s+(.*)$/);
    if (item) {
      return [
        {
          type: "item",
          marker: /\d/.test(item[2]) ? item[2] : "•",
          depth: Math.min(2, Math.floor(item[1].length / 2)),
          text: item[3],
        },
      ];
    }

    const bold = line.match(/^\s*\*\*([^*]+)\*\*\s*[:：]?\s*$/);
    if (bold) return [{ type: "heading", text: bold[1].trim() }];

    if (line.replace(/[*:：]/g, "").trim() === COACH_POINTS_TITLE) {
      return [{ type: "heading", text: COACH_POINTS_TITLE }];
    }
    return [{ type: "text", text: line.trim() }];
  });
}

// 접힌 생각 요약 한 줄 — 마지막 제목 (Gemini 생각 요약은 **제목** 으로 문단을 연다). 없으면 마지막 줄
export function getReasoningHeadline(text: string) {
  const lines = parseReasoningLines(text);
  const line =
    [...lines].reverse().find((each) => each.type === "heading") ??
    lines[lines.length - 1];
  return line ? line.text.replace(/\*\*/g, "") : "";
}

// 경과 시간 — 1분 미만은 "n초", 넘으면 "m분 n초"
export function formatElapsed(seconds: number) {
  const minutes = Math.floor(seconds / 60);
  return minutes > 0 ? `${minutes}분 ${seconds % 60}초` : `${seconds}초`;
}
